/*
 * <sticky-atc> — Vanamati sticky add-to-cart bar.
 *
 * The CTA is "Add to cart", and the form is wrapped in <product-form>, so
 * Dawn's product-form.js owns the submit: it posts to /cart/add and opens the
 * cart drawer, exactly like the main buy box. The drawer's Checkout button
 * then opens Razorpay Magic Checkout. One path everywhere:
 * ad → product → add to cart → checkout.
 *
 * (It used to be a "Buy now" with its own submit handler that added the line
 * and jumped straight to checkout. That handler, and the hidden trigger it
 * used to wake Magic Checkout, are gone.)
 *
 * What this element still does:
 *   - The main <variant-selects> + product-info.js resolve the variant; this
 *     bar drives the main picker from its own <select> and listens to the
 *     PUB_SUB `variantChange` event to mirror price / availability / selection.
 *   - It shows itself whenever the main buy box is out of view, and flags
 *     that state on <body> so base.css can move the WhatsApp bubble.
 *
 * All ids here are unique to the bar, so nothing collides with the main section.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
        this.productInfo =
          document.getElementById(`ProductInfo-${this.section}`) ||
          document.querySelector(`product-info[data-section="${this.section}"]`);

        this.select = this.querySelector('[data-sticky-variant]');
        this.idInput = this.querySelector('[data-sticky-id]');
        this.qtyInput = this.querySelector('[data-sticky-qty]');
        this.priceEl = this.querySelector(`#StickyPrice-${this.section}`);
        this.compareEl = this.querySelector(`#StickyCompare-${this.section}`);
        this.button = this.querySelector('[data-sticky-buy]');
        this.buttonText = this.button && this.button.querySelector('span');

        const variantsScript = this.querySelector('[data-sticky-variants]');
        this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

        this.setupVisibility();
        this.syncQtyRules();
        // Limits can arrive after load (the quantity-rules script), so check
        // again just before the form posts — capture phase, ahead of product-form.js.
        const form = this.querySelector('form');
        if (form) form.addEventListener('submit', () => this.syncQtyRules(), true);

        if (this.select) this.select.addEventListener('change', this.onSelectChange.bind(this));

        // PUB_SUB_EVENTS is a `const` global (constants.js), not a window property,
        // so guard with a bareword typeof — window.PUB_SUB_EVENTS is always undefined.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event || !event.data || event.data.sectionId !== this.section) return;
            this.syncFromVariant(event.data.variant);
            this.syncQtyRules();
          });
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.unsubscribe) this.unsubscribe();
        document.body.classList.remove('va-buybox-onscreen');
      }

      // Show the bar whenever the main add-to-cart is out of view — whether it's
      // scrolled above the viewport OR still below the fold (e.g. top of a mobile page).
      setupVisibility() {
        const anchor =
          (this.productInfo && this.productInfo.querySelector('.product-form__buttons')) ||
          this.productInfo;
        if (!anchor || !('IntersectionObserver' in window)) return;

        // The footer counts too: the bar would sit over the footer's links and
        // artwork, so it steps aside while any of the footer is on screen.
        const footer = document.querySelector('footer.va-ft, footer.va-footer');
        let buyboxOn = false;
        let footerOn = false;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target === footer) footerOn = entry.isIntersecting;
              else buyboxOn = entry.isIntersecting;
            });
            this.classList.toggle('is-visible', !buyboxOn && !footerOn);
            // The floating WhatsApp button sits bottom-right and, on phones, the
            // add-to-cart row lands there too — a tap on the right end of the
            // button opened WhatsApp instead of adding to the cart. Flag the
            // buy box as on screen so base.css can pull the bubble out of the
            // way; with the buy box gone, the sticky bar shows and the bubble
            // comes back above it.
            document.body.classList.toggle('va-buybox-onscreen', buyboxOn);
          },
          { threshold: 0 }
        );
        this.observer.observe(anchor);
        if (footer) this.observer.observe(footer);
      }

      // Read the formatted price (+ compare-at) straight off the matching <option>.
      applyPrice(variantId) {
        const option = this.select && this.select.querySelector(`option[value="${variantId}"]`);
        if (!option) return false;
        if (this.priceEl && option.dataset.price) this.priceEl.textContent = option.dataset.price;
        if (this.compareEl) {
          if (option.dataset.compare) {
            this.compareEl.textContent = option.dataset.compare;
            this.compareEl.hidden = false;
          } else {
            this.compareEl.textContent = '';
            this.compareEl.hidden = true;
          }
        }
        return true;
      }

      setButtonState(available) {
        if (this.button) this.button.disabled = !available;
        // Write to the inner <span>, never button.textContent — that would wipe
        // out the loading spinner the button also holds.
        if (!this.buttonText) return;
        const strings = window.variantStrings || {};
        this.buttonText.textContent = available
          ? strings.addToCart || 'Add to cart'
          : strings.soldOut || 'Sold out';
      }

      // Bar <select> changed → update the bar immediately, then drive the main picker.
      onSelectChange() {
        const variant = this.variants.find((v) => String(v.id) === String(this.select.value));
        if (!variant) return;

        // Optimistic, instant update of the bar (price/compare/id/button).
        this.applyPrice(variant.id);
        if (this.idInput) this.idInput.value = variant.id;
        this.setButtonState(variant.available);

        const mainSelects = this.productInfo && this.productInfo.querySelector('variant-selects');
        if (!mainSelects) return;

        mainSelects.querySelectorAll('fieldset').forEach((fieldset, index) => {
          const wanted = variant.options[index];
          const radio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            (el) => el.value === wanted
          );
          const dropdown = fieldset.querySelector('select');
          if (radio) {
            radio.checked = true;
          } else if (dropdown) {
            dropdown.value = wanted;
          }
        });

        // One bubbling change → <variant-selects> publishes optionValueSelectionChange,
        // product-info.js fetches + updates everything, then echoes variantChange back.
        mainSelects.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // The sticky stepper follows the main one's limits (stock and quantity
      // rules are set there, and refreshed by product-info.js before it
      // publishes variantChange), and keeps its own value inside them.
      syncQtyRules() {
        if (!this.qtyInput) return;
        const main = document.getElementById(`Quantity-${this.dataset.section}`);
        if (main) {
          ['min', 'max', 'step'].forEach((attr) => {
            if (main.hasAttribute(attr)) this.qtyInput.setAttribute(attr, main.getAttribute(attr));
            else if (attr !== 'min') this.qtyInput.removeAttribute(attr);
          });
        }
        const min = parseInt(this.qtyInput.min, 10) || 1;
        const max = parseInt(this.qtyInput.max, 10);
        let value = parseInt(this.qtyInput.value, 10) || min;
        if (value < min) value = min;
        if (max && value > max) value = max;
        this.qtyInput.value = value;
        this.qtyInput.closest('quantity-input')?.validateQtyRules?.();
      }

      syncFromVariant(variant) {
        if (!variant) {
          if (this.button) this.button.disabled = true;
          if (this.buttonText) this.buttonText.textContent = window.variantStrings
            ? window.variantStrings.unavailable
            : 'Unavailable';
          return;
        }

        if (this.idInput) this.idInput.value = variant.id;
        if (this.select && String(this.select.value) !== String(variant.id)) {
          this.select.value = variant.id;
        }

        // Prefer the formatted price from the <option>; fall back to the main price block.
        if (!this.applyPrice(variant.id)) {
          const mainPrice = this.productInfo && this.productInfo.querySelector(`#price-${this.section}`);
          if (mainPrice && this.priceEl) this.priceEl.innerHTML = mainPrice.innerHTML;
        }

        this.setButtonState(variant.available);
      }
    }
  );
}
